import 'rxjs/add/operator/filter';
import {AfterViewInit, Component, HostBinding, OnDestroy, OnInit, VERSION} from '@angular/core';
import {
    AlertService,
    AppStateService,
    AuthorPollResponse,
    BtPoll,
    Config,
    Config as C,
    ConfigResponse,
    EventObject,
    EventService,
    LoggerService,
    ModalService,
    PollUtilService,
    StoreEventsEnum,
    TakerAppState,
    TitleService
} from './modules/hbs-services';
import {environment} from '../environments/environment';
import {NavigationEnd, Router, RouterEvent} from '@angular/router';
import {appVersion, buildDate} from './appVersion';
import {WowService} from './modules/wow/wow.service';
import {dispatch, select} from '@angular-redux/store';
import {Observable} from 'rxjs/Observable';
import {OnConfigComponent} from './author/components/storeListener/onConfig.component';
import {ConfigActions} from './modules/storage';

// TODO: use DB values via config.json
// see BT_POLL_THEME_TYPE table
const THEME_TYPE_CODES: { [key: string]: string; } = {
    'default': 'mdb-skin',
    'mo': 'Modern',
    'le': 'Legacy',
    'li': 'Lime'
};
const DEFAULT_THEME_KEY = 'default';
const DEFAULT_THEME = THEME_TYPE_CODES[DEFAULT_THEME_KEY];

/**
 * This class represents the main application component. Within the @Routes annotation is the configuration of the
 * applications routes, configuring the paths for the lazy loaded components (HomeComponent, AboutComponent).
 */
@Component({
    moduleId: module.id,
    /* tslint:disable:component-selector-name */
    selector: 'body',
    templateUrl: 'app.component.html'
})
export class AppComponent extends OnConfigComponent implements OnInit, OnDestroy, AfterViewInit {

    @HostBinding('class') theme: string = DEFAULT_THEME;

    @select('poll')
    poll$: Observable<AuthorPollResponse>;

    pollTitle = '';    // poll title
    uri: string = null;        // uri (app name) from URL
    uriTitle = 'HBS Poll';   // app title for the uri
    forSignedUser: boolean = null;

    private isProd: boolean;
    private appVersion: string;
    private buildDate: string;
    private ngVersion: string;

    userFullName: string = '';
    logoutUrl: string;
    isSignedUser: boolean;

    // maps module name to its uri and title in toolbar
    private staticApps = [
        {key: '', title: 'HBS Poll Manager', uri: ''},   // see app.routes.ts
        {key: 'author', title: 'HBS Poll Manager', uri: 'author'},
        {key: 'taker', title: 'HBS Poll', uri: null/*makes link to current page*/},
        {key: 'admin', title: 'HBS Poll Admin', uri: 'admin'}
    ];


    constructor(
        private router: Router,
        private appStateService: AppStateService,
        private modalService: ModalService,
        private alertService: AlertService,
        private titleService: TitleService,
        private pollUtilService: PollUtilService,
        private eventService: EventService,
        private wowService: WowService,
        protected logger: LoggerService) {
        super(logger);
        this.logger.log('AppComponent: Environment config:', Config, 'environment.production:', environment.production);
        this.isProd = environment.production;

        this.appVersion = appVersion;
        this.buildDate = buildDate;
        this.ngVersion = VERSION.full;

        this.poll$.takeUntil(this.ngOnDestroy$).subscribe((btPollResponse: AuthorPollResponse) => {
            this.onPollChange(btPollResponse ? btPollResponse.record : null);
        });


    }

    ngOnInit() {
        super.ngOnInit();

        this.router.events.takeUntil(this.ngOnDestroy$).subscribe((event: RouterEvent) => {
            if (event instanceof NavigationEnd) {
                const urlCommand = event.url.split('/')[1];
                const prevForSignedUser = this.forSignedUser;
                if (urlCommand == 'taker') {
                    // check for open path
                    this.forSignedUser = event.url.split('/')[2] != 'open';
                } else {
                    this.forSignedUser = true;
                }
                if (prevForSignedUser == null) {
                    this.loadConfig(this.forSignedUser);
                }
                const item = this.staticApps.filter(item => item.key === urlCommand)[0];
                this.uriTitle = item ? item.title : 'HBS Poll Manager';
                this.uri = item ? item.uri : '';
            }
        });

    }

    ngAfterViewInit() {
        this.wowService.init();
    }

    public insertBtPoll() {
        this.eventService.emit(new EventObject(StoreEventsEnum.OPEN_EDIT_POLL, {isNewPoll: true}));
        return false;
    }

    public help() {
        window.open('https://inside.hbs.edu/Departments/it/howto/hbsPoll/Pages/HBS_Poll.aspx', '_blank');
        return false;
    }

    // @Override
    onConfigChanged(config: C) {

        // FIXME: this is legacy stuff used in taker only (remove when taker uses redux/store)
        this.appStateService.setConfig(<any>config as ConfigResponse);
        this.appStateService.emit(new TakerAppState('config'));
        // EOF legacy

        if (!config && this.forSignedUser != null) {
            this.loadConfig(this.forSignedUser);
        }

        this.logoutUrl = null;
        this.userFullName = '';
        this.isSignedUser = false;

        if (config) {
            const userRelatedData = this.pollUtilService.calcUserRelatedData(<any>config as ConfigResponse);
            this.isSignedUser = userRelatedData.isSignedUser;
            if (this.isSignedUser) {
                this.logoutUrl = userRelatedData ? userRelatedData.logoutUrl : null;
                this.userFullName = (config.userInfo.firstName ? config.userInfo.firstName + ' ' : '')
                    + (config.userInfo.lastName || '');
            } else {
                this.userFullName = 'Guest';
            }
        }

    }

    onPollChange(btPoll: BtPoll) {

        const themeKey = btPoll ? btPoll.themeTypeCode : DEFAULT_THEME_KEY;
        this.theme = THEME_TYPE_CODES[themeKey || DEFAULT_THEME_KEY];

        this.pollTitle = btPoll ? btPoll.pollTitle : '';
        this.titleService.setTitle(btPoll ? 'Poll: ' + btPoll.pollTitle : null);

        this.logger.log('AppComponent: onPollChange btPoll:', btPoll, 'this.theme:', this.theme);

    }

    @dispatch()
    loadConfig(forSignedUser: boolean) {
        return ConfigActions.loadConfig(forSignedUser);
    }

}
